import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileText, LoaderCircle, Save } from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import InlineFormFileUpload from "../file-upload/InlineFormFileUpload";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";

const EMPTY_FORM = {
  report_year: String(new Date().getFullYear()),
  title: "",
  status: "published",
  pdf: null,
};

const BioMedicalWasteAnnualReportFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [existingPdfUrl, setExistingPdfUrl] = useState("");
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const isEditMode = mode === "edit";

  useEffect(() => {
    if (!isEditMode) return;

    const fetchReport = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(
          `/bio-medical-waste/annual-reports/admin/${id}`,
        );
        const report = response.data?.data;
        if (!report) throw new Error("Annual report not found");
        setForm({
          report_year: String(report.report_year),
          title: report.title || "",
          status: report.status || "published",
          pdf: null,
        });
        setExistingPdfUrl(report.pdf_url || "");
      } catch (err) {
        showErrorToast(
          err.response?.data?.message || err.message || "Failed to load annual report",
        );
        navigate("/bio-medical-waste");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchReport();
  }, [id, isEditMode, navigate, showErrorToast]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => ({ ...current, [field]: "" }));
  };

  const validate = () => {
    const errors = {};
    const year = Number(form.report_year);
    if (!Number.isInteger(year) || year < 2000 || year > new Date().getFullYear() + 1) {
      errors.report_year = "Enter a valid report year.";
    }
    if (!form.title.trim()) errors.title = "Report title is required.";
    if (form.title.trim().length > 255) {
      errors.title = "Report title cannot exceed 255 characters.";
    }
    if (!isEditMode && !form.pdf) errors.pdf = "Annual report PDF is required.";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      const payload = new FormData();
      payload.append("report_year", form.report_year);
      payload.append("title", form.title.trim());
      payload.append("status", form.status);
      if (form.pdf) payload.append("pdf", form.pdf);

      if (isEditMode) {
        await apiClient.put(`/bio-medical-waste/annual-reports/${id}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Annual report updated successfully");
      } else {
        await apiClient.post("/bio-medical-waste/annual-reports", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Annual report uploaded successfully");
      }
      navigate("/bio-medical-waste");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save annual report");
    } finally {
      setIsSaving(false);
    }
  };

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" /> Loading annual report...
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none space-y-6">
      <div>
        <Button type="button" variant="ghost" className="-ml-3 mb-2 rounded-lg px-3 text-slate-500" onClick={() => navigate("/bio-medical-waste")}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Biomedical Waste
        </Button>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">
          {isEditMode ? "Edit Annual Report" : "Upload Annual Report"}
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Upload one official biomedical-waste annual report PDF for each year.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 rounded-xl border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl">
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          <div className="space-y-5">
            <div className="space-y-2">
              <label htmlFor="annual-report-title" className="text-sm font-semibold text-slate-700">Report Title</label>
              <Input id="annual-report-title" value={form.title} onChange={(event) => setField("title", event.target.value)} placeholder="Biomedical Waste Annual Report 2025" maxLength={255} className={formErrors.title ? "border-red-300" : ""} />
              <FieldError>{formErrors.title}</FieldError>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="annual-report-year" className="text-sm font-semibold text-slate-700">Report Year</label>
                <Input id="annual-report-year" type="number" min="2000" max={new Date().getFullYear() + 1} value={form.report_year} onChange={(event) => setField("report_year", event.target.value)} className={formErrors.report_year ? "border-red-300" : ""} />
                <FieldError>{formErrors.report_year}</FieldError>
              </div>

              <div className="space-y-2">
                <label htmlFor="annual-report-status" className="text-sm font-semibold text-slate-700">Publication Status</label>
                <Select value={form.status} onValueChange={(value) => setField("status", value)}>
                  <SelectTrigger id="annual-report-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Published — visible publicly</SelectItem>
                    <SelectItem value="draft">Draft — admin only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm leading-6 text-slate-600">
              <FileText className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" />
              Replacing a PDF deletes the previous server file after the update succeeds.
            </div>
          </div>

          <InlineFormFileUpload
            label="Annual Report PDF"
            value={form.pdf}
            onChange={(file) => setField("pdf", file)}
            existingFiles={existingPdfUrl ? [{
              id,
              name: `Biomedical Waste Annual Report ${form.report_year}.pdf`,
              url: existingPdfUrl,
              mimeType: "application/pdf",
            }] : []}
            accept={["application/pdf"]}
            maxSizeBytes={20 * 1024 * 1024}
            required={!isEditMode}
            error={formErrors.pdf}
            helperText="PDF only — maximum 20 MB"
          />
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" className="rounded-lg" onClick={() => navigate("/bio-medical-waste")}>Cancel</Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {isSaving ? "Saving..." : isEditMode ? "Update Annual Report" : "Upload Annual Report"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BioMedicalWasteAnnualReportFormPage;
